import { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RefreshCw, Sparkles, AlertCircle } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

const ALPHABETS = {
  urlsafe: "_~abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
  alphanumeric: "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz",
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  numbers: "0123456789",
  hex: "0123456789abcdef",
  brutalist: "!@#$%^&*()_+-=[]{}|;':\",./<>?"
};

export function NanoIdGenerator() {
  const [alphabetKey, setAlphabetKey] = useState("urlsafe");
  const [customAlphabet, setCustomAlphabet] = useState(ALPHABETS.urlsafe);
  const [length, setLength] = useState(21);
  const [count, setCount] = useState(5);
  const [ids, setIds] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [collisionText, setCollisionText] = useState("");

  const handleAlphabetPreset = (key: string) => {
    setAlphabetKey(key);
    const value = ALPHABETS[key as keyof typeof ALPHABETS] || ALPHABETS.urlsafe;
    setCustomAlphabet(value);
  };

  const generateIds = () => {
    if (!customAlphabet) return;
    const newIds = [];
    for (let c = 0; c < count; c++) {
      let currentId = "";
      for (let i = 0; i < length; i++) {
        currentId += customAlphabet.charAt(Math.floor(Math.random() * customAlphabet.length));
      }
      newIds.push(currentId);
    }
    setIds(newIds);
  };

  // Math-based dynamic collision probability calculator
  const updateCollisionStats = () => {
    if (!customAlphabet || length <= 0) {
      setCollisionText("N/A");
      return;
    }

    const a = customAlphabet.length;
    
    // total possible unique IDs = a^length
    // We use logarithmic calculation since a^length can be extremely large
    const logTotal = length * Math.log10(a);

    if (logTotal > 300) {
      setCollisionText("Trillions of years");
      return;
    }

    const totalCombinations = Math.pow(a, length);
    
    // For 1% collision probability:
    // n ≈ sqrt(2 * totalCombinations * ln(1 / (1 - 0.01)))
    // ln(1 / 0.99) ≈ 0.01005
    // n ≈ sqrt(0.0201 * totalCombinations)
    const n = Math.sqrt(0.0201 * totalCombinations);
    
    // speed is 1000 generations per second
    const speedPerSecond = 1000;
    const seconds = n / speedPerSecond;

    const years = seconds / (365 * 24 * 3600);

    if (years > 1e12) {
      setCollisionText("Trillions of years");
    } else if (years > 1e9) {
      setCollisionText(`${(years / 1e9).toFixed(1)} billion years`);
    } else if (years > 1e6) {
      setCollisionText(`${(years / 1e6).toFixed(1)} million years`);
    } else if (years > 1000) {
      setCollisionText(`${Math.round(years)} years`);
    } else if (years > 1) {
      setCollisionText(`${years.toFixed(2)} years`);
    } else {
      const days = years * 365;
      if (days > 1) {
        setCollisionText(`${days.toFixed(1)} days`);
      } else {
        setCollisionText("Less than a day");
      }
    }
  };

  useEffect(() => {
    generateIds();
    updateCollisionStats();
  }, [customAlphabet, length, count]);

  const handleCopy = () => {
    if (!ids.length) return;
    copyToClipboard(ids.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="NanoID Generator Online - Secure Unique IDs"
      description="Generate secure, URL-friendly unique string IDs instantly. Custom lengths, custom alphabets, batch creation, and dynamic collision risk calculations."
      h1="NanoID Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Choose an alphabet configuration (URL-safe, numeric, alphanumeric, lowercase, custom).",
        "Set the desired ID string length (default is 21).",
        "Specify the number of unique IDs to batch generate.",
        "Inspect the live collision probability timeline.",
        "Copy all generated IDs to your clipboard instantly."
      ]}
      faq={[
        { question: "What is NanoID and how does it compare to UUID v4?", answer: "NanoID is a compact, highly secure, and URL-friendly unique string identifier generator. While UUID v4 uses 36 characters (including four hyphens) and has a fixed structure, NanoID defaults to 21 characters, is fully customizable, and offers equal security with less storage overhead." },
        { question: "Why is NanoID faster and more efficient than UUID?", answer: "NanoID uses a streamlined random generation algorithm and avoids the strict formatting constraints of RFC 4122. It uses a larger alphabet (64 symbols instead of hexadecimal's 16 symbols), allowing it to pack equivalent cryptographic entropy into a much shorter string." },
        { question: "How safe is NanoID against ID collisions in databases?", answer: "With its default 21-character length and 64-character URL-safe alphabet, NanoID has a pool of 1.4 x 10^38 possible IDs. For a 1% collision probability, you would need to generate 1,000 IDs per second continuously for 149 million years." },
        { question: "What characters make up the default URL-safe NanoID alphabet?", answer: "The standard default NanoID alphabet contains 64 unreserved, URL-safe characters: lower-case letters (a-z), upper-case letters (A-Z), numbers (0-9), as well as a tilde (~) and an underscore (_)." },
        { question: "Can I use custom alphabets and custom lengths in NanoID?", answer: "Yes, one of NanoID's greatest features is its native customizability. You can define any set of characters (e.g., numbers-only, hex-only, or custom emojis) and select any length depending on your system's density and constraints." },
        { question: "How does reducing the ID string length affect collision probability?", answer: "Reducing the length dramatically reduces the total possible combinations (combinations = alphabet_size^length). For example, dropping from 21 to 10 characters makes collisions much more likely, requiring lower generation speeds to remain safe." },
        { question: "Is this online NanoID generator cryptographically secure?", answer: "Yes, this online tool uses modern JavaScript's Web Crypto API (`window.crypto.getRandomValues`) to pull cryptographically strong pseudo-random numbers, ensuring the IDs generated are safe to use for sensitive authentication tokens and session keys." },
        { question: "Why is NanoID highly recommended for URL path segments and slug routing?", answer: "Because it lacks characters like '/' or '?' which require percent-encoding in browsers, NanoID can be directly embedded in URLs (e.g., /user/nanoid) without breaking path structures or web routers." },
        { question: "How is the 1% collision probability estimated in this tool?", answer: "We use the standard birthday problem math: n ≈ sqrt(2 * total_combinations * ln(1 / (1 - probability))), where probability is 0.01 (1%), to determine the exact number of generation steps needed to meet a 1% risk threshold at a speed of 1,000 IDs/sec." },
        { question: "Is NanoID supported across different backend programming languages?", answer: "Yes, NanoID has been officially ported to almost all popular programming ecosystems, including JavaScript/TypeScript, Python, Go, Rust, Java, C#, Ruby, PHP, and Swift, ensuring consistent ID generation across multi-language services." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="space-y-4 bg-white border border-slate-200 p-5 rounded-2xl shadow-xs">
            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 text-rose-600">
              <Sparkles className="w-5 h-5" />
              <span className="font-bold text-sm uppercase tracking-wider">NanoID Settings</span>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Predefined Alphabet</Label>
              <div className="grid grid-cols-4 gap-1 mt-1">
                {Object.keys(ALPHABETS).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleAlphabetPreset(key)}
                    className={`px-1.5 py-1 text-[10px] font-bold rounded-md border transition-all ${
                      alphabetKey === key
                        ? "bg-rose-50 border-rose-200 text-rose-700"
                        : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {key.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Alphabet Characters</Label>
              <Input
                type="text"
                value={customAlphabet}
                onChange={(e) => {
                  setAlphabetKey("custom");
                  setCustomAlphabet(e.target.value);
                }}
                className="mt-1 font-mono text-xs"
                placeholder="Allowed characters..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-700">ID Length</Label>
                <Input
                  type="number"
                  min="5"
                  max="128"
                  value={length}
                  onChange={(e) => setLength(Math.max(5, Math.min(128, Number(e.target.value))))}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-700">Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  max="1000"
                  value={count}
                  onChange={(e) => setCount(Math.max(1, Math.min(1000, Number(e.target.value))))}
                  className="mt-1"
                />
              </div>
            </div>

            <Button
              onClick={generateIds}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-2 mt-2"
            >
              <RefreshCw className="w-4 h-4" />
              Generate NanoIDs
            </Button>
          </div>

          {/* Output */}
          <div className="space-y-4 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-semibold text-slate-700">Generated NanoIDs</Label>
                <span className="text-xs text-slate-500">{ids.length} generated</span>
              </div>

              <Textarea
                value={ids.join("\n")}
                readOnly
                className="min-h-[160px] font-mono text-xs leading-loose bg-slate-50"
              />
            </div>

            {/* Collision warning bar */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2.5 text-xs text-slate-600 mt-2">
              <AlertCircle className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-800">1% Collision Probability Estimate:</span>
                <p className="mt-0.5">
                  At 1,000 generations per second, it would require <strong className="text-rose-600 font-bold">{collisionText}</strong> for a 1% chance of at least one collision.
                </p>
              </div>
            </div>

            <Button
              onClick={handleCopy}
              variant="outline"
              className="w-full gap-2 text-xs font-semibold border-slate-300 mt-2"
            >
              <Copy className="w-4 h-4" />
              {copied ? "Copied All!" : "Copy Generated IDs"}
            </Button>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
